<template>
  <div class="compare-jobs-job-family">
    <v-menu
      v-model="menuOpen"
      location="bottom start"
      :close-on-content-click="true"
      content-class="compare-jobs-family-menu"
    >
      <template #activator="{ props: activatorProps }">
        <DpplChip
          v-bind="activatorProps"
          class="family-chip"
          :class="{ 'family-chip-open': menuOpen }"
          arrow
          :chip-height="28"
          chip-color="white"
          :is-pill="true"
        >
          <template #label>
            <span class="family-chip-label text-body-2 text-textDarkest">{{ selectedFamilyLabel }}</span>
          </template>
        </DpplChip>
      </template>
      <v-list
        min-width="240"
        class="py-0"
        density="compact"
      >
        <v-list-item
          v-for="family in familyOptions"
          :key="family.key"
          @click="handleSelectFamily(family.key)"
        >
          <v-list-item-title>{{ family.label }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <div
      v-if="profiles.length"
      class="family-profile-list"
    >
      <button
        v-for="profile in profiles"
        :key="profile.ulid"
        type="button"
        class="family-profile-row"
        @click="handleToggle(profile)"
      >
        <div
          class="family-checkbox-wrap"
          @click.stop
        >
          <DpplSimpleCheckbox
            :model-value="isSelected(profile.ulid)"
            :option="{ id: profile.ulid, label: '' }"
            hide-label
            @update:model-value="handleToggle(profile)"
          />
        </div>
        <span class="family-profile-title text-body-2 text-textDark">
          {{ profile.title }}
        </span>
        <span
          v-if="profile.jobLevelLabel"
          class="family-level-pill px-2"
          @click.stop
        >
          <DpplTooltip
            tooltip-placement="left"
            :tooltip-offset="12"
          >
            <template #activator="{ onMouseenter, onMouseleave }">
              <span
                class="family-level-pill-text text-body-3 text-textLight"
                @mouseenter="onMouseenter"
                @mouseleave="onMouseleave"
              >
                {{ profile.jobLevelLabel }}
              </span>
            </template>
            <template #tooltip-content>
              <p class="family-level-tooltip-content text-body-3 text-textLight">
                {{ profile.jobLevelTooltip }}
              </p>
            </template>
          </DpplTooltip>
        </span>
        <CompareJobsLinkedIndicator
          v-if="isLinkedProfile(profile.ulid)"
          :tooltip="linkedProfileTooltip"
          tooltip-placement="left"
        />
      </button>
    </div>
    <p
      v-else
      class="family-empty text-body-2 text-textLight"
    >
      This job family has no job profiles.
    </p>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import DpplChip from '@/components/datapeople/DpplChip.vue';
import DpplSimpleCheckbox from '@/components/datapeople/DpplSimpleCheckbox.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import CompareJobsLinkedIndicator from '@/components/jobProfiles/editor/compareJobs/CompareJobsLinkedIndicator.vue';

const props = defineProps({
  familyOptions: {
    type: Array,
    default: () => [],
  },
  selectedFamilyKey: {
    type: String,
    default: '',
  },
  selectedUlids: {
    type: Array,
    default: () => [],
  },
  linkedProfileUlids: {
    type: Array,
    default: () => [],
  },
  linkedProfileTooltip: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:selectedFamilyKey', 'toggle-profile']);

const menuOpen = ref(false);

const selectedFamilyLabel = computed(() => (
  props.familyOptions.find((family) => family.key === props.selectedFamilyKey)?.label
  || 'Uncategorized'
));

const profiles = computed(() => (
  props.familyOptions.find((family) => family.key === props.selectedFamilyKey)?.profiles || []
));

const isSelected = (ulid) => props.selectedUlids.includes(ulid);

const isLinkedProfile = (ulid) => props.linkedProfileUlids.includes(ulid);

const handleSelectFamily = (key) => {
  emit('update:selectedFamilyKey', key);

  menuOpen.value = false;
};

const handleToggle = (profile) => {
  emit('toggle-profile', {
    ulid: profile.ulid,
    title: profile.title || profile.ulid,
  });
};
</script>

<style scoped lang="less">
.compare-jobs-job-family {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  max-height: 100%;
}

.family-chip {
  border: 1px solid @stroke-default;
  width: fit-content;
  max-width: 100%;
  padding-left: 12px !important;
  padding-right: 4px !important;
  border-radius: 36px !important;

  :deep(.v-chip__overlay) {
    display: none;
  }

  :deep(.v-chip__content) {
    gap: 2px;
  }

  :deep(.ml-1) {
    margin-left: 0 !important;
  }

  :deep(.v-icon) {
    width: 16px;
    height: 16px;
    font-size: 16px;
    color: @text-light;
    transition: transform .2s;
  }
}

.family-chip-open :deep(.v-icon) {
  transform: rotate(180deg);
  margin-bottom: 2px;
}

.family-chip-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 280px;
}

.family-profile-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
  overflow-y: auto;
}

.family-profile-row {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 2px 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.family-checkbox-wrap {
  margin: 0;
  padding: 0 4px 0 0;
}

.family-profile-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.family-level-pill {
  max-width: 96px;
  border-radius: 100px;
  border: 1px solid @stroke-default;
  overflow: hidden;

  :deep(.dppl-dark-tooltip),
  :deep(.activator) {
    display: block;
    max-width: 100%;
    width: auto;
    overflow: hidden;
  }
}

.family-level-pill-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.family-level-tooltip-content {
  margin: 0;
}

.family-empty {
  margin: 0;
}

:deep(input[type=checkbox]) {
  min-width: 15px;
  min-height: 15px;
  width: 15px;
  height: 15px;

  &::before {
    left: 4px;
    width: 5px;
    height: 9px;
  }
}
</style>

<style lang="less">
.compare-jobs-family-menu.v-overlay__content > .v-list {
  box-shadow: none;
  border: 1px solid @stroke-default;
  border-radius: 4px;
  padding: 0;
  background-color: @bg-default;
  max-height: 280px;
  overflow-y: auto;
}

.compare-jobs-family-menu .v-list-item {
  padding: 8px 12px;
  min-height: unset;
}

.compare-jobs-family-menu .v-list-item-title {
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  color: @text-dark;
}
</style>
